/**
 * Whether the customer is holding the document that is on the screen.
 *
 * `Quote.sentAt` and `Invoice.sentAt` answer "was this ever sent". They cannot
 * answer the question the office actually asks, which is whether the figures
 * the customer has match the figures in front of them — and on an invoice that
 * gap is a live hazard, because an issued unpaid invoice may have its lines
 * rewritten (decision 1, amended 2026-08-23) while `sentAt` stays exactly where
 * it was and the card goes on offering "Email again".
 *
 * So a send is a row (`DocumentSend`) carrying the `contentHash` the document
 * had at the moment it went, and the comparison is two strings.
 *
 * **Never compare `updatedAt`.** It is the obvious implementation and it is
 * wrong here: `settleInvoice` writes `amountPaid` and the worker flips
 * `OVERDUE`, so the column moves on invoices nobody has edited and every
 * invoice in the system would read as changed the morning after it fell due.
 * ST Spatial reached the same conclusion from the other direction and compares
 * line timestamps instead; a hash also catches a terms or discount edit that
 * touches no line. Decision 79.
 */

/** How a document reached the customer. Mirrors `DocumentSendChannel`. */
export type DocumentSendChannelKey = 'EMAIL' | 'POST' | 'HANDED_OVER' | 'OTHER'

/** The verb, for a sentence that already names the document. */
export const SEND_CHANNEL_VERB: Record<DocumentSendChannelKey, string> = {
  EMAIL: 'emailed',
  POST: 'posted',
  HANDED_OVER: 'handed over',
  OTHER: 'sent',
}

/** The noun, for a picker. */
export const SEND_CHANNEL_LABEL: Record<DocumentSendChannelKey, string> = {
  EMAIL: 'Email',
  POST: 'Post',
  HANDED_OVER: 'Handed over',
  OTHER: 'Something else',
}

export interface DocumentSendLike {
  version: number
  channel: DocumentSendChannelKey
  contentHash: string
  toAddress?: string | null
  toName?: string | null
  sentAt: Date
  sentBy?: { name: string } | null
  /** The PDF as it went. Null where a paper send has no render behind it. */
  attachment?: { id: string; checksum: string | null; filename: string } | null
  note?: string | null
  /** The email it went as, with what the provider reported since. */
  outboundMessage?: {
    status: string
    deliveredAt: Date | null
    openedAt: Date | null
    bouncedAt: Date | null
    failureReason: string | null
  } | null
  /** The page its link opens — a proposal's or an invoice's. */
  shareLink?: SendLinkViews | null
  invoiceShareLink?: SendLinkViews | null
}

export interface SendLinkViews {
  firstViewedAt: Date | null
  lastViewedAt: Date | null
  viewCount: number
}

/**
 * What the customer did with one send, in words.
 *
 * Two sources and they mean different things, so they are never merged into
 * one "seen": **Email opened** is the provider's tracking pixel, which Apple
 * Mail fires on its own and a blocked image hides, so it is a hint; **Viewed
 * online** is the customer loading the page the email linked to, which is a
 * fact. A bounce or a failed send is the one thing the office must act on,
 * so it is its own sentence and flagged. Decision 88.
 */
export function describeSendTracking(
  send: DocumentSendLike,
  formatDate: (value: Date) => string,
): { text: string; problem: boolean } | null {
  const message = send.outboundMessage
  const link = send.shareLink ?? send.invoiceShareLink ?? null

  if (message?.status === 'FAILED') {
    return { text: `The email did not send${message.failureReason ? `: ${message.failureReason}` : '.'}`, problem: true }
  }
  if (message?.status === 'BOUNCED' || message?.bouncedAt) {
    return { text: `Bounced${message.failureReason ? ` — ${message.failureReason}` : ''}`, problem: true }
  }

  const parts: string[] = []
  if (message?.deliveredAt || message?.status === 'DELIVERED' || message?.status === 'OPENED') {
    parts.push('Delivered')
  }
  if (message?.openedAt) parts.push(`Email opened ${formatDate(message.openedAt)}`)

  if (link?.firstViewedAt) {
    const last = link.lastViewedAt ?? link.firstViewedAt
    parts.push(
      link.viewCount > 1
        ? `Viewed online ${link.viewCount} times, last ${formatDate(last)}`
        : `Viewed online ${formatDate(link.firstViewedAt)}`,
    )
  } else if (link) {
    parts.push('Not viewed online yet')
  }

  if (message?.failureReason) {
    return { text: [...parts, message.failureReason].join(' · '), problem: true }
  }

  return parts.length > 0 ? { text: parts.join(' · '), problem: false } : null
}

/**
 * `never` — nothing has gone out.
 * `sent` — the newest send carries what is on screen.
 * `changed` — it went out, and the document has been edited since.
 * `unknown` — a `sentAt` from before send tracking existed, with no send row
 *   behind it and therefore no hash to compare. Reads as sent-but-unverifiable
 *   rather than as unsent, because claiming a customer was never told about an
 *   invoice they have been chasing us over is the worse of the two errors.
 */
export type DocumentSendStatus = 'never' | 'sent' | 'changed' | 'unknown'

export interface DocumentSendState {
  status: DocumentSendStatus
  /** The most recent send, when one is recorded. */
  latest: DocumentSendLike | null
  /** How many times this document has gone out. */
  count: number
  /** Whether the office should be warned before it does anything else. */
  stale: boolean
}

/**
 * Reads the send history against the document's current content.
 *
 * `sends` may arrive in any order; the newest by version wins, because version
 * is allocated in the same transaction as the send and two sends a second
 * apart can carry the same `sentAt` to the millisecond in a test.
 */
export function documentSendState(input: {
  contentHash: string | null | undefined
  sentAt: Date | null | undefined
  sends: readonly DocumentSendLike[]
}): DocumentSendState {
  const sends = [...input.sends].sort((a, b) => b.version - a.version)
  const latest = sends[0] ?? null

  if (!latest) {
    // A document imported from ServiceM8, or sent before this shipped. The
    // date is evidence that it went; nothing says which version.
    return {
      status: input.sentAt ? 'unknown' : 'never',
      latest: null,
      count: 0,
      stale: false,
    }
  }

  // A document with no hash has never been priced through a path that writes
  // one, so there is nothing to disagree with the send.
  const changed = Boolean(input.contentHash) && latest.contentHash !== input.contentHash

  return {
    status: changed ? 'changed' : 'sent',
    latest,
    count: sends.length,
    stale: changed,
  }
}

/**
 * Whether the stored PDF says what the screen says.
 *
 * `none` — nothing has been rendered. `current` — the render was drawn from
 * the content the document has now. `outdated` — it was drawn from earlier
 * content, or before renders recorded what they were drawn from, so which
 * figures it carries is not known. The same two strings as a send, for the
 * same reason: never a timestamp. Decision 88.
 */
export type DocumentPdfState = 'none' | 'current' | 'outdated'

export function documentPdfState(input: {
  document: { id: string } | null | undefined
  documentHash: string | null | undefined
  contentHash: string | null | undefined
}): DocumentPdfState {
  if (!input.document) return 'none'
  // Nothing has priced it through a path that writes a hash, so there is
  // nothing for the render to disagree with.
  if (!input.contentHash) return 'current'
  return input.documentHash === input.contentHash ? 'current' : 'outdated'
}

/** Who it went to, in the fewest words that still identify them. */
export function sendRecipient(send: DocumentSendLike): string | null {
  if (send.toName && send.toAddress) return `${send.toName} (${send.toAddress})`
  return send.toName ?? send.toAddress ?? null
}

/**
 * The canonical text a document's content hashes to.
 *
 * Everything the customer would see on the page and nothing else. Status,
 * `amountPaid`, `sentAt` and the version counter are all deliberately out: a
 * payment settling does not change what the paper says.
 *
 * Lines are hashed in the order they print. A reorder is a real change to the
 * page, so it must change the hash.
 *
 * Declared here rather than in the API so the definition of "content" is in
 * one place and testable without a database — the same reason `job-worklist.ts`
 * lives here. The hashing itself is the API's, because `node:crypto` cannot go
 * in a bundle that ships to a technician's phone.
 */
export function documentContentSignature(document: {
  lines: ReadonlyArray<{
    description: string
    quantity: { toString(): string }
    unitPrice: number
    unitPriceIncTax?: number | null
    discountBasisPoints?: number
    taxRateBasisPoints: number
    isOptional?: boolean
    sortOrder?: number
    /** The package the line is in, on a proposal. */
    optionId?: string | null
    serviceTypeId?: string | null
  }>
  /**
   * A proposal's words and packages. Both change what the customer is agreeing
   * to — a rewritten paragraph of scope is a different proposal — so both are
   * content. Absent or empty on a plain quote and on every invoice, and then
   * the signature is byte-for-byte what it was before proposals existed, which
   * is what keeps every document sent before now reading as unchanged.
   */
  sections?: ReadonlyArray<{
    kind: string
    title?: string | null
    body?: string | null
    attachmentId?: string | null
    fileIds?: readonly string[] | null
    sortOrder?: number
  }>
  options?: ReadonlyArray<{
    id: string
    name: string
    summary?: string | null
    isRecommended?: boolean
    sortOrder?: number
  }>
  discountAmount: number
  subtotalExTax: number
  totalTax: number
  totalIncTax: number
  title?: string | null
  termsText?: string | null
  notesToCustomer?: string | null
  purchaseOrderNumber?: string | null
  validUntil?: Date | null
  issuedAt?: Date | null
  dueAt?: Date | null
}): string {
  const options = [...(document.options ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  )
  // A package by its position, never its id. Saving a proposal rewrites its
  // packages, so the ids change on every save while the page stays the same.
  const packageOf = new Map(options.map((option, index) => [option.id, index]))

  const lines = [...document.lines]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((line) =>
      [
        line.description.trim(),
        // A `Decimal` stringifies as "1.0000" and a plain number as "1". The
        // page shows the same quantity either way, so the hash must too, or
        // every document would read as changed the first time it was re-saved
        // through a different code path.
        normaliseQuantity(line.quantity),
        line.unitPrice,
        line.discountBasisPoints ?? 0,
        line.taxRateBasisPoints,
        line.isOptional ? 'optional' : '',
        // Appended only when present, so a line with neither signs exactly
        // as it did before proposals existed.
        ...(line.optionId ? [`pkg:${packageOf.get(line.optionId) ?? line.optionId}`] : []),
        ...(line.serviceTypeId ? [`svc:${line.serviceTypeId}`] : []),
        // The same: every line priced before inc prices were kept signs as it did.
        ...(line.unitPriceIncTax != null ? [`inc:${line.unitPriceIncTax}`] : []),
      ].join('\u001f'),
    )

  const sections = [...(document.sections ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((section) =>
      [
        'section',
        section.kind,
        section.title?.trim() ?? '',
        section.body?.trim() ?? '',
        section.attachmentId ?? '',
        // Appended only when there are some, so a block from before blocks
        // held several files signs exactly as it did.
        ...(section.fileIds && section.fileIds.length > 0 ? [`files:${section.fileIds.join(',')}`] : []),
      ].join('\u001f'),
    )

  const packages = options.map((option) =>
    [
      'option',
      option.name.trim(),
      option.summary?.trim() ?? '',
      option.isRecommended ? 'recommended' : '',
    ].join('\u001f'),
  )

  return [
    ...lines,
    ...packages,
    ...sections,
    `discount:${document.discountAmount}`,
    `subtotal:${document.subtotalExTax}`,
    `tax:${document.totalTax}`,
    `total:${document.totalIncTax}`,
    `title:${document.title?.trim() ?? ''}`,
    `terms:${document.termsText?.trim() ?? ''}`,
    `notes:${document.notesToCustomer?.trim() ?? ''}`,
    `po:${document.purchaseOrderNumber?.trim() ?? ''}`,
    // The dates that print on the page. `issuedAt` cannot move after a send
    // (a draft is unsendable), but `dueAt` can, and a due date the customer
    // has not been told about is exactly the kind of change this catches.
    `validUntil:${isoDay(document.validUntil)}`,
    `issuedAt:${isoDay(document.issuedAt)}`,
    `dueAt:${isoDay(document.dueAt)}`,
  ].join('\u001e')
}

/** "1.0000", "1" and " 1 " are one quantity. */
function normaliseQuantity(quantity: { toString(): string }): string {
  const text = String(quantity).trim()
  const value = Number(text)
  return Number.isFinite(value) ? String(value) : text
}

/** The day, not the millisecond: a document prints a date. */
function isoDay(value: Date | null | undefined): string {
  return value ? value.toISOString().slice(0, 10) : ''
}
